import * as React from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'wag'
    | 'wagSecondary'
    | 'wagAccent'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = '',
      variant = 'default',
      size = 'default',
      asChild = false,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'

    const variantClasses = {
      default:
        'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 active:translate-y-px font-heading rounded-2xl shadow-lg transition-all',
      destructive:
        'bg-red-500 text-white hover:bg-red-600 active:translate-y-px font-heading rounded-2xl shadow-lg transition-all',
      outline:
        'border-2 border-purple-500 text-purple-600 hover:bg-purple-500 hover:text-white font-heading rounded-2xl transition-all',
      secondary:
        'bg-gray-100 text-gray-800 hover:bg-gray-200 font-heading rounded-2xl transition-all',
      ghost:
        'hover:bg-gray-100 hover:text-gray-800 font-heading rounded-2xl transition-all',
      link: 'text-purple-600 underline-offset-4 hover:underline font-heading',
      wag: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 active:translate-y-px font-heading rounded-2xl shadow-lg transition-all',
      wagSecondary:
        'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 active:translate-y-px font-heading rounded-2xl shadow-lg transition-all',
      wagAccent:
        'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 active:translate-y-px font-heading rounded-2xl shadow-lg transition-all',
    }

    const sizeClasses = {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 rounded-md px-3',
      lg: 'h-12 rounded-2xl px-8 text-base',
      icon: 'h-10 w-10',
    }

    const classes = cn(
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      className
    )

    return <button className={classes} ref={ref} {...props} />
  }
)
Button.displayName = 'Button'

export { Button }
